"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useTenant } from "@/components/TenantProvider";

type Theme = "light" | "dark";

const ThemeContext = createContext({ theme: "dark" as Theme, toggleTheme: () => {}, setTheme: (_: Theme) => {} });

/** Resolve the initial theme: stored → tenant default → system preference → "dark" */
function resolveTheme(defaultTheme?: "light" | "dark"): Theme {
  if (typeof window === "undefined") return "dark";
  if (defaultTheme === "light" || defaultTheme === "dark") return defaultTheme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { tenant } = useTenant();
  const storageKey = `theme_${tenant.subdomain}`;

  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    const stored = localStorage.getItem(storageKey);
    if (stored === "light" || stored === "dark") return stored;
    return resolveTheme(tenant.settings.design?.defaultTheme);
  });

  // Re-sync theme when tenant switches (adara → smkn3) or when API response
  // updates the tenant data (which may carry a different defaultTheme).
  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored === "light" || stored === "dark") {
      setThemeState(stored);
    } else {
      setThemeState(resolveTheme(tenant.settings.design?.defaultTheme));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.subdomain]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem(storageKey, theme);
  }, [theme, storageKey]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme: () => setThemeState((p) => (p === "dark" ? "light" : "dark")),
        setTheme: setThemeState,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
